import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle, ExternalLink } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { statusIntent } from "@/design";
import { Button } from "@/shared/ui/button";

import type { AttentionReason } from "../api/use-orders";
import { channelName } from "../lib/detail";
import { formatBangkokDateTime } from "../lib/format";
import { sellerCenterUrl } from "../lib/seller-center-links";
import type { Order } from "../lib/types";

interface ExceptionBannerProps {
  order: Order;
  reason: AttentionReason;
  onResolve: () => void;
  /** orders:write — gates Resolve. Defaults to true. */
  canWrite?: boolean;
}

const INTENT_CLASS: Record<string, string> = {
  danger: "bg-[var(--intent-danger-bg)] text-[var(--intent-danger-fg)]",
  warning: "bg-[var(--intent-warning-bg)] text-[var(--intent-warning-fg)]",
};

// The i18n banner.* namespace names buyer_cancel_requested's block "buyer_cancel"
// (shorter, matches the mockup copy); every other reason keeps its own name.
function bannerKey(reason: AttentionReason): string {
  return reason === "buyer_cancel_requested" ? "buyer_cancel" : reason;
}

/**
 * Facts shown per reason come from the untyped `order.reviewMeta[reason]` bag
 * (BE spec 2026-09-22-mo3-seller-cancel-exceptions) — field names verified
 * against the BE service code, NOT the brief's sample (which had "mkp" for
 * "marketplace" and "tracking" for "tracking_no"). {{mkp}} itself is always
 * rendered from order.mkp via channelName().
 */
export function ExceptionBanner({
  order,
  reason,
  onResolve,
  canWrite = true,
}: ExceptionBannerProps): JSX.Element {
  const { t } = useTranslation("orders");
  const navigate = useNavigate();
  const meta = order.reviewMeta?.[reason] ?? {};
  const mkp = channelName(order.mkp);
  const intent = statusIntent("review_reason", reason);
  const key = bannerKey(reason);

  let bodyVars: Record<string, string> = { mkp };
  switch (reason) {
    case "oversold":
      bodyVars = {
        mkp,
        sku: typeof meta.sku === "string" ? meta.sku : "—",
        sellable:
          meta.sellable !== undefined && meta.sellable !== null ? String(meta.sellable) : "—",
        on_hand: meta.on_hand !== undefined && meta.on_hand !== null ? String(meta.on_hand) : "—",
        reserve: meta.reserve !== undefined && meta.reserve !== null ? String(meta.reserve) : "—",
      };
      break;
    case "buyer_cancel_requested":
      bodyVars = {
        mkp,
        by: typeof meta.respond_by === "string" ? formatBangkokDateTime(meta.respond_by) : "—",
      };
      break;
  }

  function handleOpenInventory(): void {
    const sku = typeof meta.sku === "string" ? meta.sku : "";
    void navigate({ to: "/inventory", search: { search: sku, offset: 0 } });
  }

  const sellerCenterHref = sellerCenterUrl(order.mkp, order.sale);

  return (
    <div
      role="alert"
      data-testid={`exception-banner-${reason}`}
      className={`flex flex-wrap items-start gap-3 rounded-xl px-4 py-3 text-sm ${INTENT_CLASS[intent] ?? INTENT_CLASS.warning}`}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="font-semibold">{t(`banner.${key}.title`, { mkp })}</div>
        <div className="mt-0.5">{t(`banner.${key}.body`, bodyVars)}</div>
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        {reason === "oversold" && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenInventory}
            data-testid="banner-open-inventory"
          >
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
            {t("btn_open_inventory")}
          </Button>
        )}
        {reason === "buyer_cancel_requested" && sellerCenterHref && (
          <Button variant="outline" size="sm" asChild data-testid="banner-open-seller-center">
            <a href={sellerCenterHref} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              {t("btn_open_seller_center")}
            </a>
          </Button>
        )}
        {canWrite && (
          <Button
            variant="outline"
            size="sm"
            onClick={onResolve}
            data-testid={`banner-resolve-${reason}`}
          >
            {t("btn_resolve")}
          </Button>
        )}
      </div>
    </div>
  );
}
